import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Download, Mail, Leaf, Sparkles, ChevronLeft, Zap } from "lucide-react";
import { API } from "@/App";

const DetoxFreebie = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const [downloadUrl, setDownloadUrl] = useState("");

  const highlights = [
    { icon: Leaf,       text: "Understand what's actually happening in your body" },
    { icon: Sparkles,   text: "Daily rituals + reflection prompts for calm energy" },
    { icon: CheckCircle2, text: "A simple reset for hormones, sleep, and clarity" },
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("submitting");
    try {
      const res = await axios.post(`${API}/freebie/3-day-detox`, { name, email });
      setDownloadUrl(res.data.download_url || "/downloads/3-day-detox-sequence.docx");
      setStatus("success");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
      setStatus("idle");
    }
  };

  return (
    <div
      className="min-h-screen bg-[#F4EFE7] pt-28 pb-20 px-6 md:px-12"
      data-testid="detox-freebie-page"
    >
      {/* Decorative warm glow */}
      <div
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none -z-0"
        style={{
          background:
            "radial-gradient(ellipse at 20% 30%, rgba(197,160,89,0.15) 0%, transparent 55%), radial-gradient(ellipse at 90% 80%, rgba(44,53,36,0.10) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#4A5543] hover:text-[#C5A059] mb-10 transition-colors"
          data-testid="freebie-back-home"
        >
          <ChevronLeft className="w-4 h-4" />
          Back home
        </Link>

        <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Copy */}
          <div className="md:col-span-7">
            <span className="overline mb-4 block text-[#8A6D2E]">
              <Sparkles className="inline-block w-3.5 h-3.5 mr-2" />
              Free Guide
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#2C3524] leading-[1.05] mb-6">
              The <span className="text-[#C5A059] italic">Decode Guide.</span>
            </h1>
            <p className="text-[#4A5543] text-lg md:text-xl font-light leading-relaxed mb-6 max-w-xl">
              A gentle, science-informed reset designed for high-achieving women in
              perimenopause — restore your energy, calm your nervous system, and reconnect
              with the woman under the exhaustion.
            </p>

            <ul className="space-y-3 mb-10 max-w-xl">
              {highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-[#2C3524]">
                  <h.icon className="w-5 h-5 mt-0.5 text-[#C5A059]" strokeWidth={1.5} />
                  <span className="font-light">{h.text}</span>
                </li>
              ))}
            </ul>

            {status !== "success" && (
              <form
                onSubmit={onSubmit}
                className="max-w-xl bg-white rounded-3xl shadow-[0_20px_60px_rgba(44,53,36,0.12)] p-6 md:p-8 border border-[#E3DAC9]"
                data-testid="freebie-form"
              >
                <p className="text-[#2C3524] font-medium mb-4">
                  Where should I send it?
                </p>
                <div className="grid gap-3 mb-4">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your first name"
                    className="w-full px-4 py-3 rounded-full border border-[#E3DAC9] bg-[#F9F8F6] text-[#2C3524] placeholder:text-[#4A5543]/50 focus:outline-none focus:border-[#C5A059] transition-colors"
                    data-testid="freebie-name-input"
                  />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your best email"
                    className="w-full px-4 py-3 rounded-full border border-[#E3DAC9] bg-[#F9F8F6] text-[#2C3524] placeholder:text-[#4A5543]/50 focus:outline-none focus:border-[#C5A059] transition-colors"
                    data-testid="freebie-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="btn-primary w-full sm:w-auto text-base group disabled:opacity-60"
                  data-testid="freebie-submit"
                >
                  {status === "submitting" ? (
                    "Sending…"
                  ) : (
                    <>
                      Send Me the Guide
                      <ArrowRight className="inline-block ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                <p className="mt-4 text-xs text-[#4A5543]/70">
                  No spam, ever. Unsubscribe with one click.
                </p>
              </form>
            )}

            {status === "success" && (
              <div
                className="max-w-xl bg-white rounded-3xl shadow-[0_20px_60px_rgba(44,53,36,0.12)] p-8 border border-[#E3DAC9]"
                data-testid="freebie-thankyou"
              >
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-6">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-heading text-3xl text-[#2C3524] mb-3">
                  It's on its way.
                </h2>
                <p className="text-[#4A5543] mb-6 leading-relaxed">
                  Check your inbox for your welcome email. In the meantime, you can grab
                  the workbook right here:
                </p>
                <a
                  href={downloadUrl}
                  download
                  className="btn-primary inline-flex items-center gap-2 mb-4"
                  data-testid="freebie-download-btn"
                >
                  <Download className="w-4 h-4" />
                  Download the Decode Guide
                </a>
                <p className="text-xs text-[#4A5543]/70 flex items-center gap-1.5 mt-4 mb-6">
                  <Mail className="w-3.5 h-3.5" />
                  A copy will also land in your inbox shortly.
                </p>

                {/* Warm-lead nudge → Reset Type quiz */}
                <div
                  className="mt-2 rounded-2xl border border-[#C5A059]/40 bg-[#F9F8F6] p-5"
                  data-testid="freebie-quiz-nudge"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#C5A059]/15 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <p className="text-[#2C3524] font-medium mb-1">
                        Ready for step 2?
                      </p>
                      <p className="text-sm text-[#4A5543] mb-4 font-light">
                        Take the 3-minute Reset Type quiz next — you'll get a personalized
                        roadmap based on the pattern behind your symptoms.
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          window.dispatchEvent(new Event("open-quiz-modal"))
                        }
                        className="inline-flex items-center gap-2 text-[#C5A059] font-medium text-sm hover:gap-3 transition-all"
                        data-testid="freebie-quiz-cta"
                      >
                        Take the Reset Type Quiz
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Visual */}
          <div className="md:col-span-5 relative">
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(44,53,36,0.25)] ring-1 ring-[#2C3524]/5">
              <img
                src="/assets/ivana-freebie.png"
                alt="Ivana Silva — 3-Day Detox Reset"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C3524]/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-heading text-white/95 text-2xl md:text-3xl italic leading-tight">
                  "A gentle reset<br />is the beginning<br />of a new season."
                </p>
                <p className="mt-3 text-[#DCC286] text-xs tracking-[0.2em] uppercase">
                  — Ivana Silva
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetoxFreebie;
